"use client";

import Link from "next/link";
import { ArrowRight, Building2, Calendar, Users, Globe, Crown, Briefcase, Handshake } from "lucide-react";
import { Container, Section } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import {
  HubHero, HubOverview, HubFeatures, HubUseCases, HubWhy, HubFAQ, HubCTA,
  CategoryHubComposer,
} from "@/components/ui/category-hub";
import {
  companyStats, companyChildren, companyFeatures, companyUseCases,
  companyWhy, companyFaq, companyCta, timeline, leadershipRoles,
} from "@/lib/data/company-hub";

/* ============================================================
   BESPOKE 1 — Company signature diagram.
   Central CMZ node with concentric rings showing
   years / clients / engines / uptime / incidents.
   ============================================================ */
function CompanySignatureDiagram() {
  const ROUND = (n: number) => Math.round(n * 100) / 100;
  const cx = 260;
  const cy = 260;
  return (
    <div className="relative w-full max-w-[520px] aspect-square mx-auto">
      <svg viewBox="0 0 520 520" className="w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="cCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#dc143c" stopOpacity="0.85" />
            <stop offset="60%" stopColor="#8b5cf6" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r="200" fill="url(#cCore)" />
        {/* Concentric rings — each represents a metric */}
        {[
          { r: 230, label: "15+ YEARS", color: "rgb(220 20 60 / 0.3)" },
          { r: 195, label: "300+ CLIENTS", color: "rgb(139 92 246 / 0.3)" },
          { r: 160, label: "5 ENGINES", color: "rgb(6 182 212 / 0.3)" },
          { r: 125, label: "50+ PLATFORMS", color: "rgb(16 185 129 / 0.3)" },
          { r: 90, label: "0 INCIDENTS", color: "rgb(245 158 11 / 0.3)" },
        ].map((ring, i) => (
          <g key={i}>
            <circle cx={cx} cy={cy} r={ring.r} fill="none" stroke={ring.color} strokeWidth="0.7" strokeDasharray="2 5" />
          </g>
        ))}
        {/* Tick marks at quarter points */}
        {Array.from({ length: 24 }).map((_, i) => {
          const angle = (i / 24) * Math.PI * 2;
          const x1 = ROUND(cx + Math.cos(angle) * 225);
          const y1 = ROUND(cy + Math.sin(angle) * 225);
          const x2 = ROUND(cx + Math.cos(angle) * 232);
          const y2 = ROUND(cy + Math.sin(angle) * 232);
          return <line key={`t-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgb(255 255 255 / 0.25)" strokeWidth="0.5" />;
        })}
        {/* Center hub */}
        <g transform={`translate(${cx}, ${cy})`}>
          <circle r="65" fill="rgb(10 10 18 / 0.95)" />
          <circle r="55" fill="none" stroke="rgb(220 20 60 / 0.5)" strokeWidth="1" />
          <circle r="42" fill="none" stroke="rgb(255 255 255 / 0.1)" strokeWidth="0.6" />
          <text x="0" y="-2" textAnchor="middle" fontSize="20" fontWeight="900" fill="white" letterSpacing="-0.5">
            CMZ
          </text>
          <text x="0" y="14" textAnchor="middle" fontSize="5.5" fontWeight="700" fill="#dc143c" letterSpacing="2">
            CRYPTOMIZE
          </text>
          <text x="0" y="24" textAnchor="middle" fontSize="4.5" fill="rgb(255 255 255 / 0.5)" letterSpacing="1.5">
            15+ · 300+ · 5
          </text>
        </g>
        {/* Quadrant labels */}
        {[
          { angle: -Math.PI / 4, label: "15+ YRS", color: "var(--color-crimson)" },
          { angle: Math.PI / 4, label: "300+ CLIENTS", color: "var(--color-purple)" },
          { angle: (3 * Math.PI) / 4, label: "5 ENGINES", color: "var(--color-cyan)" },
          { angle: -(3 * Math.PI) / 4, label: "0 INCIDENTS", color: "var(--color-amber)" },
        ].map((q, i) => {
          const r = 213;
          const x = ROUND(cx + Math.cos(q.angle) * r);
          const y = ROUND(cy + Math.sin(q.angle) * r);
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="22" fill="rgb(10 10 18 / 0.85)" stroke={q.color} strokeWidth="0.7" />
              <text x={x} y={y + 3} textAnchor="middle" fontSize="7" fontWeight="800" fill="white" letterSpacing="0.4">
                {q.label}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="absolute top-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "0.5s" }}>
        <Building2 className="h-3.5 w-3.5" style={{ color: "var(--color-crimson)" }} />
        <span className="text-xs font-semibold">CryptoMize</span>
      </div>
      <div className="absolute top-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "1.5s" }}>
        <Calendar className="h-3.5 w-3.5" style={{ color: "var(--color-amber)" }} />
        <span className="text-xs font-semibold">15+ Years</span>
      </div>
      <div className="absolute bottom-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "2.5s" }}>
        <Users className="h-3.5 w-3.5" style={{ color: "var(--color-purple)" }} />
        <span className="text-xs font-semibold">300+ Clients</span>
      </div>
      <div className="absolute bottom-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "3.5s" }}>
        <Crown className="h-3.5 w-3.5" style={{ color: "var(--color-cyan)" }} />
        <span className="text-xs font-semibold">Elite Tier</span>
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 2 — Company timeline.
   Horizontal cinematic timeline with milestone cards.
   ============================================================ */
function CompanyTimeline() {
  return (
    <div className="relative">
      {/* Horizontal connector line */}
      <div className="hidden lg:block absolute top-[3rem] left-0 right-0 h-px bg-gradient-to-r from-transparent via-[var(--color-crimson)] to-transparent opacity-30" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        {timeline.map((m, i) => (
          <Reveal key={m.year} delay={i * 80}>
            <div className="relative h-full">
              <div
                className="relative grid place-items-center h-24 w-24 rounded-full mx-auto mb-4 transition-transform hover:scale-110"
                style={{
                  background: `linear-gradient(135deg, color-mix(in oklch, var(--color-${m.accent}) 30%, transparent), color-mix(in oklch, var(--color-${m.accent}) 8%, transparent))`,
                  boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${m.accent}) 50%, transparent), 0 8px 24px -10px var(--color-${m.accent})`,
                }}
              >
                <span className="text-lg font-bold font-mono-num" style={{ color: `var(--color-${m.accent})` }}>
                  {m.year}
                </span>
              </div>
              <div className="text-center">
                <h3 className="text-sm font-bold mb-2">{m.title}</h3>
                <p className="text-[11px] leading-relaxed text-[rgb(var(--fg-muted))]">
                  {m.body}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 3 — Leadership role grid.
   ============================================================ */
function LeadershipGrid() {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {leadershipRoles.map((r, i) => (
        <Reveal key={r.name} delay={i * 60}>
          <div
            className="glass rounded-2xl p-5 h-full card-glow"
            style={{ ["--glow" as string]: `var(--color-${r.accent})` }}
          >
            <div className="flex items-start gap-3">
              <span
                className="grid place-items-center h-10 w-10 rounded-lg shrink-0"
                style={{
                  background: `color-mix(in oklch, var(--color-${r.accent}) 18%, transparent)`,
                  boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${r.accent}) 35%, transparent)`,
                }}
              >
                <span className="text-sm font-bold" style={{ color: `var(--color-${r.accent})` }}>◆</span>
              </span>
              <div>
                <h3 className="text-sm font-bold">{r.name}</h3>
                <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: `var(--color-${r.accent})` }}>
                  {r.role}
                </p>
                <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{r.focus}</p>
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/* ============================================================
   PAGE COMPOSER
   ============================================================ */
export function CompanyHubPage() {
  return (
    <CategoryHubComposer
      overviewWrap="crimson"
      featuresWrap="purple"
      useCasesWrap="emerald"
      whyWrap="cyan"
      faqWrap="amber"
      ctaWrap="violet"
      hero={
        <section id="top" className="relative overflow-hidden pt-8 pb-16 sm:pt-12 sm:pb-24">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute top-10 left-1/4 h-72 w-72 rounded-full bg-[var(--color-crimson)] opacity-22 blur-[130px] animate-pulse-glow" />
            <div className="absolute top-32 right-1/4 h-80 w-80 rounded-full bg-[var(--color-purple)] opacity-18 blur-[140px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
            <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[var(--color-emerald)] opacity-15 blur-[140px] animate-pulse-glow" style={{ animationDelay: "4s" }} />
          </div>
          <Container size="wide">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center">
              <div className="flex flex-col items-start gap-6">
                <Reveal>
                  <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full glass">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--color-emerald)] opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-emerald)]" />
                    </span>
                    <span className="eyebrow-tiny uppercase tracking-[0.28em]">CryptoMize · The Organization</span>
                  </div>
                </Reveal>
                <Reveal delay={80}>
                  <h1 className="display-hero text-balance">
                    The Organization{" "}
                    <span className="bg-gradient-to-br from-[var(--color-crimson)] via-[var(--color-purple)] to-[var(--color-amber)] bg-clip-text text-transparent">
                      Behind Perception X2
                    </span>
                  </h1>
                </Reveal>
                <Reveal delay={160}>
                  <p className="text-[1.35rem] sm:text-[1.5rem] leading-[1.45] font-medium text-[rgb(var(--fg))] max-w-2xl">
                    CryptoMize is the engineering company that built and operates the world's most advanced autonomous perception amplification platform. 15+ years. 300+ elite clients. Five proprietary engines.
                  </p>
                </Reveal>
                <Reveal delay={220}>
                  <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-2xl">
                    We are not a marketing agency. We are not a PR firm. We are an engineering company that builds and operates the technology platform — with the precision of a defense contractor and the innovation velocity of a technology pioneer.
                  </p>
                </Reveal>
                <Reveal delay={280}>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      href="/about"
                      className="btn-primary-gradient h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-crimson)/30]"
                    >
                      About CryptoMize <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      href="/contact"
                      className="btn-glass h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                    >
                      Contact Our Team
                    </Link>
                  </div>
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl w-full">
                    {companyStats.map((s) => (
                      <div
                        key={s.label}
                        className="glass rounded-2xl p-3 card-glow"
                        style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
                      >
                        <div className="display-stat-sm" style={{ color: `var(--color-${s.accent})` }}>
                          <Counter to={s.to} suffix={s.suffix ?? ""} decimals={s.decimals ?? 0} />
                        </div>
                        <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold uppercase tracking-wider">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              </div>
              <Reveal delay={200} className="order-first lg:order-last">
                <CompanySignatureDiagram />
              </Reveal>
            </div>
          </Container>
        </section>
      }
      overview={
        <HubOverview
          eyebrow="The Company Surfaces"
          title="Every Page of the CryptoMize Story"
          description="The company hub gathers every corporate surface — from About and Mission to Leadership, Careers, Partners, Press, and operational status."
          items={companyChildren}
          accent="crimson"
        />
      }
      features={
        <>
          <Section id="timeline" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Company Timeline"
                title="From Vision to Global Platform"
                description="Six milestones across 15+ years of building the perception amplification category. From founding insight to the 144-dimensional reference standard."
                accent="crimson"
              />
              <CompanyTimeline />
            </Container>
          </Section>
          <Section id="leadership" className="relative mt-8">
            <Container>
              <SectionHeading
                eyebrow="Leadership"
                title="The Functions That Steer CryptoMize"
                description="Six senior leadership functions cover strategic direction, engineering, security, engagement, research, and operations."
                accent="purple"
              />
              <LeadershipGrid />
            </Container>
          </Section>
          <Section id="features" className="relative mt-8">
            <Container>
              <SectionHeading
                eyebrow="What Defines Us"
                title="The Culture That Compounds"
                description="Eight attributes define how CryptoMize operates — each contributes to the long-term trust that anchors every engagement."
                accent="amber"
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {companyFeatures.map((f, i) => (
                  <Reveal key={f.title} delay={i * 50}>
                    <div
                      className="glass rounded-2xl p-5 h-full card-glow"
                      style={{ ["--glow" as string]: `var(--color-${f.accent})` }}
                    >
                      <div
                        className="grid place-items-center h-10 w-10 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, var(--color-${f.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 35%, transparent)`,
                        }}
                      >
                        <span className="text-base font-bold" style={{ color: `var(--color-${f.accent})` }}>◆</span>
                      </div>
                      <h3 className="mt-3 text-base font-bold">{f.title}</h3>
                      <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Container>
          </Section>
        </>
      }
      useCases={
        <HubUseCases
          eyebrow="Engagement Patterns"
          title="Six Client Archetypes"
          description="CryptoMize serves six primary engagement archetypes — each with bespoke operational tuning, security posture, and engagement model."
          cases={companyUseCases}
          accent="emerald"
        />
      }
      why={
        <HubWhy
          eyebrow="Why CryptoMize"
          title="The Differentiators That Compound"
          items={companyWhy}
          accent="cyan"
        />
      }
      faq={
        <HubFAQ
          eyebrow="Company FAQ"
          title="Frequently Asked Questions"
          items={companyFaq}
          accent="amber"
        />
      }
      cta={
        <HubCTA
          eyebrow={companyCta.eyebrow}
          title={companyCta.title}
          description={companyCta.description}
          primary={companyCta.primary}
          secondary={companyCta.secondary}
          trustItems={companyCta.trustItems}
          accent="violet"
        />
      }
    />
  );
}
